import { useEffect } from 'react';
import isFullyVisibleInContainer from '../utils/isFullyVisibleInContainer';
import { getFocusableFormElements } from '../utils/focusableElements';

/**
 * Focuses the first focus-able element, if one exists.
 * If none exists, we will focus on the close icon. In the case where a modal is set to not
 * have a close icon, we will fallback to the modal title.
 * @param {*} contentRef - ref attached to the modals content container. Not the entire modal including overlay.
 * @param {*} closeRef - ref attached to the close icon which will auto-focus on if there is no other focusable element visible.
 * @param {*} titleRef - ref attached to the title which will auto-focus on if there is no close icon or any other focusable element visible.
 * @param {React.Ref} [overrideAutofocusRef=null] - ref attached to an element in the modal content which will get auto-focus if it is fully visible.
 * This will take precedence over the close icon and the title.
 */
export default function useAutofocus({
    closeRef,
    contentRef,
    overrideAutofocusRef,
    titleRef,
}) {
    useEffect(() => {
        if (!contentRef?.current) return;

        const focusedElement = document.activeElement;

        /**
         * An element is focusable if it exists and is fully visible in the viewport.
         * Find and focus an element in this priority:
         * 1. Override element
         * 2. Non-button form element
         * 3. Modal primary button
         * 4. Close button
         * 5. Title
         */

        const focusableElements = getFocusableFormElements(contentRef.current);

        // Below is a hack. Instead, we should be passing primaryButtonRef to the hook rather than finding it ourselves.
        // However, the current implementation of Button does not allow us to put a ref on it.
        const primaryButton = Array.from(
            contentRef.current.querySelectorAll(
                '[data-test="modal-primary-button"]'
            )
        ).filter(el => window.getComputedStyle(el).display !== 'none')[0];

        if (
            overrideAutofocusRef?.current &&
            isFullyVisibleInContainer(
                contentRef.current,
                overrideAutofocusRef.current
            )
        ) {
            overrideAutofocusRef.current.focus();
        } else if (
            typeof focusableElements[0]?.focus == 'function' &&
            isFullyVisibleInContainer(contentRef.current, focusableElements[0])
        ) {
            focusableElements[0].focus();
        } else if (
            primaryButton &&
            isFullyVisibleInContainer(contentRef.current, primaryButton)
        ) {
            primaryButton.focus();
        } else if (closeRef?.current) {
            closeRef.current.focus();
        } else if (titleRef?.current) {
            titleRef.current.focus();
        }

        // Return focus to whatever was focused before the Modal opened
        return () => {
            if (
                focusedElement &&
                document.body.contains(focusedElement) &&
                focusedElement.focus
            ) {
                focusedElement.focus();
            }
        };
    }, [contentRef, closeRef, titleRef, overrideAutofocusRef]);
}
